import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, UrlTree } from '@angular/router';
import { Observable } from 'rxjs';

import { resolvePagePath } from '@core/routing/resolve-page-path';
import {
  isModernFrameNamespaceSegment,
  resolveModernFramePagePath,
  toCanonicalModernUrl
} from '@platform/contracts';

import { PageAccessService, type PageAccessResult } from './page-access.service';

export type ShellPagePathKind = 'legacy' | 'modern' | 'none';

export interface ShellPagePathResolution {
  readonly kind: ShellPagePathKind;
  readonly pagePath: string | null;
}

export interface ModernAccessContext {
  onAllowed(): void;
  onAccessDenied(): void;
  onAuthRequired(): void;
  onUnavailable(): void;
}

@Injectable({
  providedIn: 'root'
})
export class RoutePageAccessPolicy {
  readonly #pageAccess = inject(PageAccessService);
  readonly #router = inject(Router);
  readonly #authorizedPagePaths = new Set<string>();

  isModernFrameRoute(url: string): boolean {
    const canonical = toCanonicalModernUrl(url);
    const path = canonical ?? url;
    const pathname = path.split(/[?#]/, 1)[0] ?? '';
    const withoutHostedPrefix = pathname.startsWith('/_modern/')
      ? pathname.slice('/_modern'.length)
      : pathname;
    const namespace = withoutHostedPrefix.split('/').filter(Boolean)[0];
    return namespace !== undefined && isModernFrameNamespaceSegment(namespace);
  }

  resolveShellPagePath(input: {
    url: string;
    route?: ActivatedRouteSnapshot;
  }): ShellPagePathResolution {
    if (this.isModernFrameRoute(input.url)) {
      return {
        kind: 'modern',
        pagePath: resolveModernFramePagePath(input.url)
      };
    }

    if (input.route) {
      return {
        kind: 'legacy',
        pagePath: resolvePagePath(input.route)
      };
    }

    return { kind: 'none', pagePath: null };
  }

  checkAccess(pagePath: string): Observable<PageAccessResult> {
    return this.#pageAccess.checkDetailed(pagePath);
  }

  mapLegacyGuardResult(result: PageAccessResult): boolean | UrlTree {
    switch (result) {
      case 'allowed':
        return true;
      case 'denied':
      case 'unavailable':
        return this.#router.parseUrl('/access-denied');
      case 'auth-required':
        return this.#router.parseUrl('/login');
    }
  }

  mapModernHostResult(result: PageAccessResult, ctx: ModernAccessContext): void {
    switch (result) {
      case 'allowed':
        ctx.onAllowed();
        break;
      case 'denied':
        ctx.onAccessDenied();
        break;
      case 'auth-required':
        ctx.onAuthRequired();
        break;
      case 'unavailable':
        ctx.onUnavailable();
        break;
    }
  }

  hasAuthorizedGrant(pagePath: string): boolean {
    return this.#authorizedPagePaths.has(pagePath);
  }

  grantPagePath(pagePath: string): void {
    this.#authorizedPagePaths.add(pagePath);
  }

  clearGrants(): void {
    this.#authorizedPagePaths.clear();
  }
}
